import { useQuery } from '@tanstack/react-query';
import { apiFetch } from './api.js';

export interface ApiMentionSource {
  document_id: string | null;
  source_url: string | null;
  document_type: string | null;
  document_date: string | null;
  source_slug: string | null;
  source_display_name: string | null;
}

export interface ApiMention {
  role: string;
  confidence: string;
  evidence_excerpt: string | null;
  /** Observation origin (extracted_accounts.source_adapter); 'customer_feedback' for applied customer reports, null on the contact surface. */
  source_adapter: string | null;
  section: { label: string; text: string } | null;
  source: ApiMentionSource;
}

export interface ApiEnrichmentMention {
  entity_string: string;
  resolved_name: string;
  resolved_role: string | null;
  reasoning: string | null;
  evidence_urls: string[];
  research_method: string | null;
  source: string;
  confidence: string;
  as_of_date: string | null;
}

export interface ApiProjectMentionsResponse {
  enrichment: ApiEnrichmentMention[];
  mentions: ApiMention[];
}

export type BadgeVariant = 'blue' | 'green' | 'amber' | 'red' | 'purple' | 'gray';

// Mirrors KIND_META in lib/mock-accounts.ts so a role badge in the evidence
// drawer matches the account-type badge used everywhere else.
const ROLE_META: Record<string, { label: string; variant: BadgeVariant }> = {
  developer: { label: 'Developer', variant: 'blue' },
  owner: { label: 'Owner', variant: 'gray' },
  architect: { label: 'Architect', variant: 'purple' },
  gc: { label: 'GC', variant: 'green' },
  engineer: { label: 'Engineer', variant: 'amber' },
  law_firm: { label: 'Law firm', variant: 'gray' },
  consultant: { label: 'Consultant', variant: 'gray' },
  landscape_architect: { label: 'Landscape architect', variant: 'green' },
  other: { label: 'Other', variant: 'gray' },
  developer_contact: { label: 'Developer contact', variant: 'blue' },
  architect_contact: { label: 'Architect contact', variant: 'purple' },
  engineer_contact: { label: 'Engineer contact', variant: 'amber' },
  gc_contact: { label: 'GC contact', variant: 'green' },
  agency_pm: { label: 'Agency PM', variant: 'gray' },
  attorney: { label: 'Attorney', variant: 'gray' },
  owner_contact: { label: 'Owner contact', variant: 'gray' },
};

/**
 * Highlight tint per badge variant, applied to the <mark> around the matched
 * excerpt. Static literal strings so Tailwind's scanner picks them up; gray
 * uses zinc to match the Badge gray variant in @parcel/ui.
 */
export const MENTION_MARK_CLASSES: Record<BadgeVariant, string> = {
  blue: 'bg-blue-100 text-blue-900 ring-blue-200 dark:bg-blue-400/15 dark:text-blue-200 dark:ring-blue-400/25',
  purple:
    'bg-purple-100 text-purple-900 ring-purple-200 dark:bg-purple-400/15 dark:text-purple-200 dark:ring-purple-400/25',
  amber:
    'bg-amber-100 text-amber-900 ring-amber-200 dark:bg-amber-400/15 dark:text-amber-200 dark:ring-amber-400/25',
  green:
    'bg-green-100 text-green-900 ring-green-200 dark:bg-green-400/15 dark:text-green-200 dark:ring-green-400/25',
  red: 'bg-red-100 text-red-900 ring-red-200 dark:bg-red-400/15 dark:text-red-200 dark:ring-red-400/25',
  gray: 'bg-zinc-100 text-zinc-800 ring-zinc-200 dark:bg-zinc-400/15 dark:text-zinc-200 dark:ring-zinc-400/25',
};

export function mentionRoleMeta(role: string): { label: string; variant: BadgeVariant } {
  return ROLE_META[role] ?? { label: role, variant: 'gray' };
}

export type MentionEntityKind = 'account' | 'contact';

export function useProjectMentions(
  entityKind: MentionEntityKind,
  entityId: string | null,
  projectId: string | null,
  workspaceId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ['project-mentions', entityKind, entityId, projectId, workspaceId],
    enabled: enabled && Boolean(entityId) && Boolean(projectId) && Boolean(workspaceId),
    queryFn: async () => {
      const qs = new URLSearchParams({ workspace_id: workspaceId ?? '' }).toString();
      const base = entityKind === 'account' ? 'accounts' : 'contacts';
      return apiFetch<ApiProjectMentionsResponse>(
        `/${base}/${encodeURIComponent(entityId ?? '')}/projects/${encodeURIComponent(projectId ?? '')}/mentions?${qs}`,
      );
    },
  });
}
